import React, { useState, useEffect } from "react";
import { CircleX, Save } from "lucide-react";
import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";

const EditTestCaseModal = ({ isOpen, onClose, testCase, onSave, isLoading }) => {
  const [formData, setFormData] = useState({
    testCase: "",
    httpMethod: "",
    expectedStatusCode: "",
    expectedResult: "",
    testType: "",
    priority: "",
    tags: "",
  });

  useEffect(() => {
    if (isOpen && testCase) {
      setFormData({
        testCase: testCase.testCase || "",
        httpMethod: testCase.httpMethod || "",
        expectedStatusCode: testCase.expectedStatusCode || "",
        expectedResult: testCase.expectedResult || "",
        testType: testCase.testType || "",
        priority: testCase.priority || "",
        tags: testCase.tags || "",
      });
    }
  }, [isOpen, testCase]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const updatedData = {
      id: testCase.id,
      ...formData,
    };
    onSave(updatedData);
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "8px",
          boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: "1px solid #e0e0e0",
          backgroundColor: "#fafafa",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span className="font-apple text-lg text-gray-800 flex items-center">
          <p>Edit Test Case</p>
        </span>

        <button
          onClick={onClose}
          className="flex items-center justify-center p-1.5 rounded hover:bg-gray-200 transition-all duration-200 cursor-pointer"
        >
          <CircleX size={20} className="text-red-500" />
        </button>
      </DialogTitle>

      <DialogContent
        sx={{
          marginTop: "20px",
          backgroundColor: "#ffffff",
        }}
      >
        <form onSubmit={handleSubmit} className="font-apple">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Test Case Description <span className="text-red-500">*</span>
              </label>
              <textarea
                name="testCase"
                value={formData.testCase}
                onChange={handleChange}
                rows={2}
                className="w-full px-3 py-2 border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm resize-none"
                placeholder="Enter test case description"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  HTTP Method <span className="text-red-500">*</span>
                </label>
                <select
                  name="httpMethod"
                  value={formData.httpMethod}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm bg-white"
                  required
                >
                  <option value="">Select Method</option>
                  <option value="GET">GET</option>
                  <option value="POST">POST</option>
                  <option value="PUT">PUT</option>
                  <option value="DELETE">DELETE</option>
                  <option value="PATCH">PATCH</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Expected Status Code <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="expectedStatusCode"
                  value={formData.expectedStatusCode}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm"
                  placeholder="e.g., 200, 201, 404"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Test Type <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="testType"
                value={formData.testType}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm"
                placeholder="e.g., positive, negative, integration, custom-type..."
                required
              />
              <p className="mt-1 text-xs text-gray-500">
                Enter any test type that describes your test case
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tags
              </label>
              <input
                type="text"
                name="tags"
                value={formData.tags}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm"
                placeholder="e.g., urgent, api, backend, regression (comma-separated)"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Expected Result <span className="text-red-500">*</span>
              </label>
              <textarea
                name="expectedResult"
                value={formData.expectedResult}
                onChange={handleChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm resize-none"
                placeholder="Describe the expected outcome of this test case"
                required
              />
            </div>
          </div>
        </form>
      </DialogContent>

      <DialogActions
        sx={{
          padding: "16px 24px",
          borderTop: "1px solid #e0e0e0",
          backgroundColor: "#fafafa",
          gap: "12px",
        }}
      >
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-all duration-200 cursor-pointer"
          disabled={isLoading}
        >
          Cancel
        </button>
        <button
          type="submit"
          onClick={handleSubmit}
          disabled={isLoading}
          className="flex items-center px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 transition-all duration-200 text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save size={16} className="mr-2" />
          {isLoading ? "Saving..." : "Save Changes"}
        </button>
      </DialogActions>
    </Dialog>
  );
};

export default EditTestCaseModal;